/**
 * API Client for Propensify Frontend
 * 
 * Single Responsibility: Handle all HTTP communication with the API.
 * - Injects Clerk JWT into requests
 * - Handles auth errors globally (401, 403)
 * - Provides typed fetch wrapper
 * 
 * Usage:
 *   import { apiClient } from '@/lib/api-client';
 *   const data = await apiClient.get<ProspectSummary>('/api/v1/prospects/123/summary/');
 */

// API base URL from environment
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '';

/**
 * Error thrown when API returns an error response
 */
/**
 * The words of an error: body.detail ?? body.error ?? statusText (R9).
 * statusText is empty over HTTP/2, so the server's own message comes first.
 */
export function apiErrorWords(body: unknown, statusText: string): string {
  if (body && typeof body === 'object') {
    const { detail, error } = body as { detail?: unknown; error?: unknown };
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (typeof error === 'string' && error.trim()) return error.trim();
  }
  return statusText;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown,
    /** Full request URL (best-effort) */
    public url?: string,
    /** HTTP method (best-effort) */
    public method?: string,
    /** True if request included an Authorization header (token value is never stored) */
    public hasAuthHeader?: boolean,
    /** Response headers (best-effort; useful for debugging proxies/CDNs) */
    public responseHeaders?: Record<string, string>
  ) {
    super(`API Error ${status}: ${apiErrorWords(body, statusText)}`);
    this.name = 'ApiError';
  }

  private get detailString(): string | null {
    const body = this.body;
    if (!body) return null;

    if (typeof body === 'string') return body;

    if (typeof body === 'object' && body !== null) {
      const detail = (body as { detail?: unknown }).detail;
      return typeof detail === 'string' ? detail : null;
    }

    return null;
  }

  /** True if user's account is not provisioned in Django */
  get isNotProvisioned(): boolean {
    if (this.status !== 403) return false;
    const detail = (this.detailString || '').toLowerCase();
    return detail.includes('account not provisioned');
  }

  /** True if the user or org has insufficient credits for the requested action */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }

  /** True if auth token is invalid or expired */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True if request is forbidden for another reason (e.g., lacks client/prospect permissions) */
  get isForbidden(): boolean {
    return this.status === 403 && !this.isNotProvisioned;
  }

  /** True if resource was not found */
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/**
 * Request options for API calls
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

interface FormDataRequestOptions extends Omit<RequestInit, 'body'> {
  body?: FormData;
}

/**
 * Get the Clerk session token for API requests.
 * Must be called from a Client Component context.
 * 
 * @param getToken - The getToken function from useAuth() hook
 */
async function getAuthToken(getToken: () => Promise<string | null>): Promise<string | null> {
  try {
    return await getToken();
  } catch (error) {
    console.error('[API Client] Failed to get auth token:', error);
    return null;
  }
}

/**
 * Create headers for API request
 */
function createHeaders(token: string | null, customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders);
  
  headers.set('Content-Type', 'application/json');
  
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  
  return headers;
}

/**
 * Create headers for binary fetches (no Content-Type).
 */
function createBinaryHeaders(token: string | null, customHeaders?: HeadersInit): Headers {
  const headers = new Headers(customHeaders);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

/**
 * Parse API response
 */
async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type');
  
  if (contentType?.includes('application/json')) {
    return response.json();
  }
  
  // Return empty object for non-JSON responses
  return {} as T;
}

/**
 * Handle API error responses
 */
async function handleErrorResponse(
  response: Response,
  request: { url?: string; method?: string }
): Promise<never> {
  let body: unknown;
  
  // Clone the response so we can try both json and text parsing
  const text = await response.text();
  
  try {
    body = JSON.parse(text);
  } catch {
    body = text;
  }
  
  const responseHeaders: Record<string, string> = {};
  try {
    for (const [k, v] of response.headers.entries()) {
      responseHeaders[k] = v;
    }
  } catch {
    // ignore
  }

  const apiError = new ApiError(
    response.status,
    response.statusText,
    body,
    request.url || response.url,
    request.method,
    undefined,
    Object.keys(responseHeaders).length > 0 ? responseHeaders : undefined
  );

  // Log every API error to the browser console so failures are visible without opening Network tab.
  console.error(
    `[API] ${request.method ?? 'GET'} ${request.url ?? response.url} → ${response.status} ${response.statusText}`,
    body,
  );

  throw apiError;
}
/**
 * Make an authenticated API request
 * 
 * @param endpoint - API endpoint (e.g., '/api/v1/prospects/123/summary/')
 * @param options - Fetch options
 * @param getToken - Clerk getToken function from useAuth()
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {},
  getToken: () => Promise<string | null>
): Promise<T> {
  const token = await getAuthToken(getToken);
  
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = createHeaders(token, options.headers);
  
  const fetchOptions: RequestInit = {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  };
  
  let response: Response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (networkError) {
    console.error(
      `[API] ${fetchOptions.method ?? 'GET'} ${url} → network error (no response)`,
      networkError,
    );
    throw networkError;
  }
  
  if (!response.ok) {
    const hasAuthHeader = !!token;

    try {
      await handleErrorResponse(response, {
      url,
      method: (fetchOptions.method || options.method || 'GET') as string,
      });
    } catch (err) {
      // Attach request context to ApiError (without leaking token value)
      if (err instanceof ApiError) {
        err.hasAuthHeader = hasAuthHeader;
      }
      throw err;
    }
  }
  
  return parseResponse<T>(response);
}

export async function apiRequestFormData<T>(
  endpoint: string,
  options: FormDataRequestOptions = {},
  getToken: () => Promise<string | null>
): Promise<T> {
  const token = await getAuthToken(getToken);
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = new Headers(options.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(url, {
    ...options,
    method: options.method ?? 'POST',
    headers,
    body: options.body,
  });
  if (!response.ok) {
    await handleErrorResponse(response, { url, method: (options.method || 'POST') as string });
  }
  return parseResponse<T>(response);
}

/**
 * Make an authenticated API request and return a Blob.
 */
export async function apiRequestBlob(
  endpoint: string,
  options: RequestOptions = {},
  getToken: () => Promise<string | null>
): Promise<Blob> {
  const token = await getAuthToken(getToken);
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = createBinaryHeaders(token, options.headers);

  const fetchOptions: RequestInit = {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  };

  const response = await fetch(url, fetchOptions);
  if (!response.ok) {
    const hasAuthHeader = !!token;
    try {
      await handleErrorResponse(response, {
        url,
        method: (fetchOptions.method || options.method || 'GET') as string,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        err.hasAuthHeader = hasAuthHeader;
      }
      throw err;
    }
  }

  return response.blob();
}

/**
 * Create a bound API client with the getToken function
 * 
 * Usage in components:
 *   const { getToken } = useAuth();
 *   const api = createApiClient(getToken);
 *   const data = await api.get<ProspectSummary>('/api/v1/prospects/123/summary/');
 */
export function createApiClient(getToken: () => Promise<string | null>) {
  return {
    get: <T>(endpoint: string, options?: RequestOptions) =>
      apiRequest<T>(endpoint, { ...options, method: 'GET' }, getToken),
    
    getBlob: (endpoint: string, options?: RequestOptions) =>
      apiRequestBlob(endpoint, { ...options, method: 'GET' }, getToken),

    postBlob: (endpoint: string, body?: unknown, options?: RequestOptions) =>
      apiRequestBlob(
        endpoint,
        {
          ...options,
          method: 'POST',
          body,
          // DRF requires Content-Type for JSON parsing; blob download endpoints still accept JSON bodies.
          headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
        },
        getToken
      ),

    post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
      apiRequest<T>(endpoint, { ...options, method: 'POST', body }, getToken),

    postFormData: <T>(endpoint: string, body: FormData, options?: FormDataRequestOptions) =>
      apiRequestFormData<T>(endpoint, { ...options, method: 'POST', body }, getToken),
    
    put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
      apiRequest<T>(endpoint, { ...options, method: 'PUT', body }, getToken),
    
    patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
      apiRequest<T>(endpoint, { ...options, method: 'PATCH', body }, getToken),
    
    delete: <T>(endpoint: string, options?: RequestOptions) =>
      apiRequest<T>(endpoint, { ...options, method: 'DELETE' }, getToken),
  };
}

/**
 * Type for the API client object
 */
export type ApiClient = ReturnType<typeof createApiClient>;

